import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabaseClient } from '@/lib/supabase-client';
import { useToast } from '@/hooks/use-toast';

export interface RealtimeNotificationPayload {
  id: string;
  user_id: string;
  organization_id: string | null;
  type: string;
  title: string;
  body: string | null;
  data: Record<string, unknown> | null;
  read: boolean;
  created_at: string;
}

/**
 * Subscribes to Supabase Realtime postgres_changes on the notifications table
 * for the given user_id. New rows are shown as toasts.
 * Ensure Supabase auth session is set (e.g. after login) so RLS allows the stream.
 */
export function useRealtimeNotifications(userId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const channelRef = useRef<ReturnType<typeof supabaseClient.channel> | null>(null);

  useEffect(() => {
    if (!userId) return;

    const channel = supabaseClient
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const row = (payload as { new: unknown }).new as RealtimeNotificationPayload;
          const variant = row.type === 'error' ? 'destructive' : row.type === 'success' ? 'success' : 'default';
          toast({
            title: row.title,
            description: row.body ?? undefined,
            variant,
          });
          queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('[useRealtimeNotifications] Channel error – check Supabase Realtime and RLS.');
        }
      });

    channelRef.current = channel;

    return () => {
      supabaseClient.removeChannel(channel);
      channelRef.current = null;
    };
  }, [userId, toast, queryClient]);
}
